import { OrganizationAPI } from '@business/api/organization';
import { Blocs } from '@business/blocs';
import { OrderProductId, OrderProductResponse, ProductPageQuery } from '@business/entities/order';
import { OrgAddressResponse, OrgId } from '@business/entities/organization';
import { ProjectServiceResponse } from '@business/entities/project';
import { CommonService } from '@business/services';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { R } from '@core/utils/r';
import { LoadingState } from '@core/utils/repository/loading_state';
import { BehaviorSubject, combineLatest, forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { orderAddresses } from './AddEditTimeMaterialPage';
import { TimeMaterialId, TimeMaterialResponse } from '@business/entities/tm';
import { TMBoardId } from '@business/entities/tm/TimeMaterialBoardResponse';
import { PaginatedController } from '@core/utils/paginated';
import { OrderPackageResponse } from '@business/entities/order/OrderPackageResponse';
import { QBSalesTaxResponse } from '@business/entities/salesTax';
import { TimeMaterialAPI } from '@business/api/timeMaterial';

export class AddEditTimeMaterialPageBloc extends BaseBloc {
  private readonly userBloc = resolve(Blocs.user);
  private readonly organizationBloc = resolve(Blocs.organization);
  private readonly sClassBloc = resolve(Blocs.serviceClass);
  private readonly timeMaterialBloc = resolve(Blocs.timeMaterial);
  private readonly productBloc = resolve(Blocs.orderProduct);
  private readonly termBloc = resolve(Blocs.orderTerm);
  private readonly vendorBloc = resolve(Blocs.orderVendor);
  private readonly stageBloc = resolve(Blocs.tmStage);
  private readonly tierBloc = resolve(Blocs.pricingTeir);
  private readonly serviceBloc = resolve(Blocs.service);
  private readonly labourTypeBloc = resolve(Blocs.labourTypes);
  private readonly tmBoards = resolve(Blocs.timeMaterialBoard);
  private readonly packageBloc = resolve(Blocs.orderPackage);
  private readonly tmFeeBloc = resolve(Blocs.tmFee);

  constructor(
    private readonly orgId: OrgId,
    private readonly boardId: TMBoardId,
    private readonly orderId?: TimeMaterialId,
    private readonly isInvoice?: boolean,
  ) {
    super();
  }

  loading = new LoadingState();
  users$ = this.userBloc.staff$;

  selectOrg$ = (orgId?: OrgId) => (orgId ? this.organizationBloc.selectOrganization(orgId) : null);

  products$ = new BehaviorSubject<OrderProductResponse[]>([]);
  services$ = new BehaviorSubject<ProjectServiceResponse[]>([]);
  classes$ = this.sClassBloc.classes$;
  terms$ = this.termBloc.terms$;
  vendors$ = this.vendorBloc.vendors$;
  tiers$ = this.tierBloc.tiers$;
  stages$ = this.stageBloc.all$;
  organization$ = this.organizationBloc.selectOrganization(this.orgId);
  order$ = this.timeMaterialBloc.selectOrder(this.orderId || '0');
  addresses$ = new BehaviorSubject<OrgAddressResponse[]>([]);
  labourTypes$ = this.labourTypeBloc.labourTypes$;
  tmBoards$ = this.tmBoards.boards$.pipe(map(boards => boards.filter(b => b.id != '9999')));
  orderSubject$ = new BehaviorSubject<TimeMaterialResponse | null>(null);
  tmFees$ = this.tmFeeBloc.tmFees$;

  salesTax$ = new BehaviorSubject<QBSalesTaxResponse[]>([]);

  onInit() {
    return forkJoin([
      this.userBloc.fetchUsers(),
      this.organizationBloc.fetchOrganization(this.orgId),
      this.orderId ? this.timeMaterialBloc.fetchOrder(this.orderId, false, this.isInvoice) : of(null),
      this.productBloc.fetchProductsPaginated({}).pipe(tap(products => this.products$.next(products.data))),
      this.termBloc.fetchTerms(),
      this.tmFeeBloc.fetchTMFees(),
      this.sClassBloc.fetchClasses(),
      this.vendorBloc.fetchVendors(),
      this.fetchStages(this.boardId),
      this.tierBloc.fetchTiers(),
      this.paginated.fetch(),
      this.labourTypeBloc.fetchLabourTypes(),
      this.tmBoards.fetchTimeMaterialBoards(),
      this.serviceBloc.fetchServices().pipe(tap(services => this.services$.next(services))),
      OrganizationAPI.fetchAddresses(this.orgId, []).pipe(
        tap(addresses => {
          this.addresses$.next(addresses);
          orderAddresses.next(addresses);
        }),
      ),
      TimeMaterialAPI.getSalesTaxOptions().pipe(tap(data => this.salesTax$.next(data))),
    ]).pipe(this.loading.run());
  }

  fetchStages = (boardId: TMBoardId) => {
    return this.stageBloc.fetchStages(boardId);
  };
  createOrder = this.timeMaterialBloc.createOrder;
  updateEstimate = this.timeMaterialBloc.updateEstimate;
  updateInvoice = this.timeMaterialBloc.updateInvoice;
  createInvoice = this.timeMaterialBloc.createInvoice;

  loadAddresses = (ids: string[]) =>
    OrganizationAPI.fetchAddresses(this.orgId, ids).pipe(
      tap(addresses => {
        this.addresses$.next(addresses);
        orderAddresses.next(addresses);
      }),
    );

  fetchOrganization = (orgId: OrgId) => {
    return this.organizationBloc.fetchOrganization(orgId);
  };

  searchOrganizations = (search: string) => {
    return this.organizationBloc.searchOrganizations(search).pipe(map(data => CommonService.selectItems(data.data, 'name', 'id')));
  };

  //   searchProducts = (search: string, products?: OrderProductId[]) => {
  //     return this.productBloc.searchProducts(search, products).pipe(
  //       tap(data => {
  //         this.products$.next(R.uniqBy([...this.products$.value, ...data.data], p => p.id));
  //       }),
  //       map(data =>
  //         CommonService.selectGroupedItems(
  //           [
  //             {
  //               label: 'Products',
  //               items: data.data.map(p => ({ label: `${p.sku} - ${p.title}`, value: `product-${p.id}` })),
  //             },
  //             {
  //               label: 'Services',
  //               items: this.services$.value
  //                 .filter(s => s.title.toLowerCase().includes(search.toLowerCase()) || R.includes(products, s.id))
  //                 .map(s => ({ label: `${s.title}`, value: `service-${s.id}` })),
  //             },
  //           ],
  //           'label',
  //           s => s.items,
  //           'label',
  //           'value',
  //         ),
  //       ),
  //     );
  //   };

  fetchFn = (query: ProductPageQuery) => {
    return this.packageBloc.fetchPackagesPaginated(query).pipe(this.loading.run());
  };

  readonly paginated = new PaginatedController<OrderPackageResponse, string, ProductPageQuery>(this.fetchFn, '', {
    page: 1,
  });

  readonly packages$ = this.paginated.items$;

  searchProducts = (search: string, products?: OrderProductId[], activeProducts = false) => {
    return combineLatest([this.productBloc.searchProducts(search, products), this.packageBloc.searchPackages(search)]).pipe(
      map(([productsData, packagesData]) => {
        const packagesProducts = R.flatMap(packagesData.data, p => p.products);
        this.packages$.next(R.uniqBy([...this.packages$.value, ...packagesData.data], p => p.id));
        if (activeProducts) {
          this.products$.next(R.uniqBy([...productsData.data.filter(p => p.active), ...packagesProducts.filter(p => p.active)], p => p.id));
        } else {
          this.products$.next(R.uniqBy([...productsData.data, ...packagesProducts], p => p.id));
        }

        return CommonService.selectGroupedItems(
          [
            {
              label: 'Products',
              items: this.products$.value
                .filter(
                  s =>
                    s.title.toLowerCase().includes(search.toLowerCase()) ||
                    s.sku.toLowerCase().includes(search.toLowerCase()) ||
                    R.includes(products, s.id),
                )
                .map(s => ({ label: `${s.title}`, value: `product-${s.id}` })),
            },
            {
              label: 'Services',
              items: this.services$.value
                .filter(s => s.title.toLowerCase().includes(search.toLowerCase()) || R.includes(products, s.id))
                .map(s => ({ label: `${s.title}`, value: `service-${s.id}` })),
            },
            {
              label: 'Packages',
              items: packagesData.data
                .filter(s => s.title.toLowerCase().includes(search.toLowerCase()) || R.includes(products, s.id))
                .map(s => ({ label: `${s.title}`, value: `package-${s.id}` })),
            },
          ],
          'label',
          s => s.items,
          'label',
          'value',
        );
      }),
    );
  };

  onReloadOrder = (orderId: TimeMaterialId) => {
    return this.timeMaterialBloc.fetchOrder(orderId, false, this.isInvoice).pipe(
      map(data => {
        this.orderSubject$.next(data);
      }),
    );
  };
}
